/**
 * WinbackAlgorithmConfig
 *
 * Guided editor for the win-back scoring algorithm. Designed to be easy to
 * teach: a plain-language intro, one-click presets, a LIVE example player
 * that shows exactly how the current settings score a player and what comp
 * they'd get, the everyday controls up front, and the fiddly math tucked
 * under "Advanced".
 *
 * Fully controlled: pass `config` and `onChange`.
 */
import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  RotateCcw,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Sparkles,
  User,
} from 'lucide-react';
import {
  DEFAULT_WINBACK_CONFIG,
  computeValueScore,
  getRecencyMultiplier,
  computeFinalScore,
  computeComp,
  getCompColor,
} from '../lib/winbackAlgorithm';

// Everyday "what makes a player worth winning back" controls.
const WEIGHT_FIELDS = [
  { key: 'hoursWeight', label: 'Hours played', hint: 'Total time at the tables' },
  { key: 'avgWagerWeight', label: 'Avg wager', hint: 'Typical bet size / stakes' },
  { key: 'visitsWeight', label: 'Visits', hint: 'Separate days they came in' },
  { key: 'visitGapWeight', label: 'Visit frequency', hint: 'Rewards players who used to come often' },
  { key: 'sessionsWeight', label: 'Sessions', hint: 'Times they sat down to play' },
];

const RECENCY_FIELDS = [
  { key: 'recencyBoost15to45', label: '15-45 days' },
  { key: 'recencyBoost46to90', label: '46-90 days' },
  { key: 'recencyBoost91to180', label: '91-180 days' },
  { key: 'recencyBoost180plus', label: '180+ days' },
];

const SCALE_FIELDS = [
  { key: 'maxHours', label: 'Hours for full credit' },
  { key: 'maxVisits', label: 'Visits for full credit' },
  { key: 'maxSessions', label: 'Sessions for full credit' },
  { key: 'maxAvgWager', label: 'Avg wager ($) for full credit' },
  { key: 'visitGapReference', label: 'Frequency reference (days)' },
];

// One-click starting points. Each sets the five factor weights; everything
// else is left as-is so an admin can still fine-tune.
const PRESETS = [
  {
    name: 'Balanced',
    desc: 'A bit of everything',
    weights: { hoursWeight: 40, avgWagerWeight: 25, visitsWeight: 20, visitGapWeight: 10, sessionsWeight: 5 },
  },
  {
    name: 'Favor big spenders',
    desc: 'Higher stakes win the biggest comps',
    weights: { hoursWeight: 25, avgWagerWeight: 50, visitsWeight: 15, visitGapWeight: 5, sessionsWeight: 5 },
  },
  {
    name: 'Favor frequent regulars',
    desc: 'Reward the players who used to come in a lot',
    weights: { hoursWeight: 30, avgWagerWeight: 10, visitsWeight: 30, visitGapWeight: 25, sessionsWeight: 5 },
  },
];

const DEFAULT_EXAMPLE = {
  TotalHours: 250,
  TotalVisits: 40,
  TotalSessions: 60,
  AvgWager: 50,
  AvgDaysBetweenVisits: 4,
  DaysSinceLastPlay: 30,
};

const EXAMPLE_FIELDS = [
  { key: 'TotalHours', label: 'Hours', step: 1 },
  { key: 'TotalVisits', label: 'Visits', step: 1 },
  { key: 'TotalSessions', label: 'Sessions', step: 1 },
  { key: 'AvgWager', label: 'Avg wager $', step: 1 },
  { key: 'AvgDaysBetweenVisits', label: 'Avg gap (days)', step: 0.1 },
  { key: 'DaysSinceLastPlay', label: 'Days lapsed', step: 1 },
];

export default function WinbackAlgorithmConfig({
  config,
  onChange,
  onSaveDefault,
  onReset,
  onSyncFromReport,
}) {
  const c = { ...DEFAULT_WINBACK_CONFIG, ...(config || {}) };
  const set = (key, value) => onChange({ ...c, [key]: value });
  const applyPreset = (p) => onChange({ ...c, ...p.weights });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [example, setExample] = useState(DEFAULT_EXAMPLE);
  const setEx = (key, value) => setExample((e) => ({ ...e, [key]: value }));

  // Live scoring of the example player with the CURRENT settings.
  const exValue = computeValueScore(example, c);
  const exRecency = getRecencyMultiplier(example.DaysSinceLastPlay, c);
  const exFinal = computeFinalScore(exValue, exRecency);
  const exComp = computeComp(exFinal, example.AvgWager, c);
  const exColor = getCompColor(exComp.amount);

  const weightTotal = WEIGHT_FIELDS.reduce((s, f) => s + (parseFloat(c[f.key]) || 0), 0);
  const tiers = Array.isArray(c.compTiers) ? c.compTiers : [];
  const setTier = (idx, field, value) => {
    const next = tiers.map((t, i) =>
      i === idx ? { ...t, [field]: value, label: field === 'amount' ? `$${value}` : t.label } : t
    );
    set('compTiers', next);
  };
  const addTier = () => set('compTiers', [...tiers, { minScore: 10, amount: 10, label: '$10' }]);
  const removeTier = (idx) => set('compTiers', tiers.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      {/* How it works */}
      <div className="text-sm text-gray-400 bg-gray-900/40 border border-gray-700 rounded-lg p-3 leading-relaxed">
        Each lapsed player gets a <span className="text-white">value score (0-100)</span> from the factors below,
        multiplied by a <span className="text-white">recency boost</span> based on how long ago they left. That
        <span className="text-white"> final score</span> picks a comp tier, and their average wager nudges the exact
        dollar amount. Use a preset to start, then watch the example below to see the effect.
      </div>

      {/* Presets */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={15} className="text-amber-400" />
          <h4 className="text-sm font-semibold text-white">Quick presets</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => applyPreset(p)}
              className="text-left px-3 py-2 rounded-lg bg-gray-700/60 hover:bg-gray-700 border border-gray-600 transition-colors"
            >
              <div className="text-sm font-medium text-white">{p.name}</div>
              <div className="text-xs text-gray-400">{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* LIVE EXAMPLE */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <User size={16} className="text-amber-400" />
          <h4 className="text-sm font-semibold text-white">Example player</h4>
          <span className="text-xs text-gray-400">edit these to test your settings</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          {EXAMPLE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] text-gray-400 mb-1">{f.label}</label>
              <input
                type="number"
                step={f.step}
                value={example[f.key]}
                onChange={(e) => setEx(f.key, parseFloat(e.target.value) || 0)}
                className="w-full px-2 py-1.5 bg-gray-900 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div className="bg-gray-800 rounded-lg p-2.5">
            <div className="text-xl font-bold text-white">{exValue}</div>
            <div className="text-[11px] text-gray-400">Value score</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-2.5">
            <div className="text-xl font-bold text-blue-300">{exRecency}x</div>
            <div className="text-[11px] text-gray-400">Recency boost</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-2.5">
            <div className="text-xl font-bold text-amber-400">{exFinal}</div>
            <div className="text-[11px] text-gray-400">Final score</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-2.5">
            <div className={`text-xl font-bold ${exComp.amount > 0 ? exColor.text : 'text-gray-500'}`}>
              {exComp.amount > 0 ? `$${exComp.amount}` : 'No comp'}
            </div>
            <div className="text-[11px] text-gray-400">Recommended comp</div>
          </div>
        </div>
      </div>

      {/* Factor importance */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-white">What makes a player worth winning back?</h4>
          <span className="text-xs text-gray-500">relative importance</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          These are weighed against each other — a factor at 40 counts twice as much as one at 20. Set any to 0 to ignore it.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {WEIGHT_FIELDS.map((f) => {
            const pct = weightTotal > 0 ? Math.round(((parseFloat(c[f.key]) || 0) / weightTotal) * 100) : 0;
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-300">
                    {f.label} <span className="text-gray-500">— {f.hint}</span>
                  </span>
                  <span className="text-amber-400 font-medium">{pct}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={c[f.key]}
                  onChange={(e) => set(f.key, parseInt(e.target.value, 10))}
                  className="w-full accent-amber-500"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Recency boost */}
      <div>
        <h4 className="text-sm font-semibold text-white mb-1">Recency boost</h4>
        <p className="text-xs text-gray-500 mb-3">
          Multiplies the value score by how recently the player lapsed. A boost above 1 raises the comp; below 1 lowers it.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {RECENCY_FIELDS.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>{f.label}</span>
                <span className="text-amber-400 font-medium">{c[f.key]}x</span>
              </div>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={c[f.key]}
                onChange={(e) => set(f.key, parseFloat(e.target.value))}
                className="w-full accent-amber-500"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Comp tiers */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-white">Comp amounts</h4>
          <button
            type="button"
            onClick={addTier}
            className="flex items-center gap-1 px-2 py-1 bg-gray-700 text-gray-200 rounded text-xs hover:bg-gray-600"
          >
            <Plus size={12} /> Add tier
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          A player at or above each final score gets that comp (avg wager then nudges it up or down a little).
        </p>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-gray-500 px-1">
            <span className="col-span-6">Final score of at least</span>
            <span className="col-span-5">Comp amount ($)</span>
            <span className="col-span-1"></span>
          </div>
          {tiers.map((t, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <input
                type="number"
                min="0"
                value={t.minScore}
                onChange={(e) => setTier(idx, 'minScore', parseFloat(e.target.value) || 0)}
                className="col-span-6 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
              />
              <input
                type="number"
                min="0"
                value={t.amount}
                onChange={(e) => setTier(idx, 'amount', parseFloat(e.target.value) || 0)}
                className="col-span-5 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
              />
              <button
                type="button"
                onClick={() => removeTier(idx)}
                className="col-span-1 flex items-center justify-center text-gray-400 hover:text-red-400"
                title="Remove tier"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced (collapsed) */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700/40"
        >
          {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          Advanced tuning
          <span className="text-xs text-gray-500">— you usually won't need these</span>
        </button>
        {showAdvanced && (
          <div className="px-3 pb-4 pt-1 space-y-4 border-t border-gray-700">
            <div>
              <h5 className="text-xs font-semibold text-gray-300 mb-1">Full-credit thresholds</h5>
              <p className="text-[11px] text-gray-500 mb-2">
                The value at which a factor counts as "maxed out" (100%). E.g. hours = 300 means 300+ hours gives full hours credit.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {SCALE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="block text-[11px] text-gray-400 mb-1">{f.label}</label>
                    <input
                      type="number"
                      min="1"
                      value={c[f.key]}
                      onChange={(e) => set(f.key, parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-gray-300 mb-1">Avg-wager comp nudge</h5>
              <p className="text-[11px] text-gray-500 mb-2">
                Within a comp tier, a higher-than-par wager bumps the amount up, lower bumps it down, capped by the max swing.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Par wager ($)</label>
                  <input
                    type="number"
                    min="1"
                    value={c.wagerAdjustReference}
                    onChange={(e) => set('wagerAdjustReference', parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Max swing (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={Math.round((parseFloat(c.wagerAdjustMaxPct) || 0) * 100)}
                    onChange={(e) => set('wagerAdjustMaxPct', (parseFloat(e.target.value) || 0) / 100)}
                    className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Round to ($)</label>
                  <input
                    type="number"
                    min="1"
                    value={c.compRounding}
                    onChange={(e) => set('compRounding', parseFloat(e.target.value) || 1)}
                    className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {(onSaveDefault || onReset || onSyncFromReport) && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-700">
          {onSaveDefault && (
            <button
              type="button"
              onClick={onSaveDefault}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-gray-900 rounded-lg text-sm font-medium hover:bg-amber-400"
            >
              <Save size={14} /> Save as Default
            </button>
          )}
          {onSyncFromReport && (
            <button
              type="button"
              onClick={onSyncFromReport}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg text-sm hover:bg-gray-600"
            >
              <RefreshCw size={14} /> Sync from report config
            </button>
          )}
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg text-sm hover:bg-gray-600"
            >
              <RotateCcw size={14} /> Reset to Defaults
            </button>
          )}
        </div>
      )}
    </div>
  );
}
